import { useEffect, useState } from 'react';
import { formatInputDisplay, parseLocaleNumber } from 'helpers/numberFormat';

const asNumber = (value) =>
  value === null || value === undefined || value === '' ? null : Number(value);

/**
 * The text a numeric input shows, kept apart from the number it stands for.
 *
 * A controlled input that displays its parent's value cannot hold a half-typed
 * "85," — the parent has to store the raw text for the field to stay editable.
 * When that parent is a shared store (the app-wide certificate price), the raw
 * text leaks into every request priced from it. This keeps the text here and
 * hands the parent a number only when the text reads as one, or `null` when
 * the field is cleared; a half-typed value never leaves the input.
 *
 * The text follows `value` when it changes from outside (a suggestion, a reset,
 * another writer) — unless it already says that number, so typing "85," over 85
 * is not overwritten.
 *
 * @param {number|string|null|undefined} value  The number the input stands for.
 * @param {(value: number|null) => void} onValue  Called with each complete value.
 * @param {{ blankZero?: boolean }} [options]  Show 0 as an empty field.
 * The third element puts the text back to `value`: for a field left empty
 * whose parent kept its number (a required figure), so it does not read blank.
 *
 * @returns {[string, (text: string) => void, () => void]}
 */
export function useNumericText(value, onValue, { blankZero = false } = {}) {
  const display = (v) => (blankZero && asNumber(v) === 0 ? '' : formatInputDisplay(v));
  const [text, setText] = useState(() => display(value));

  useEffect(() => {
    if (parseLocaleNumber(text) !== asNumber(value)) setText(display(value));
    // Only an outside change of `value` should rewrite the text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onText = (next) => {
    setText(next);
    if (String(next).trim() === '') return onValue(null);
    const parsed = parseLocaleNumber(next);
    if (parsed !== null) onValue(parsed);
  };

  const resync = () => setText(display(value));

  return [text, onText, resync];
}

export default useNumericText;
